import { roundMoneyDiv } from '@/app/utils/moneyRounding';
import { formatTenThousandths, parseTenThousandths } from './moneyArithmetic';

const groupThousands = (digits: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const currencySymbol = (currency: string): string =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? currency;

/** Rounds a decimal string of up to four places to two places with BigInt arithmetic only.
 *  A value that is not such a string is returned unchanged rather than guessed at. */
export const formatMoneyDisplay = (amount: string, options: { grouped?: boolean } = {}): string => {
    const tenThousandths = parseTenThousandths(amount);
    if (tenThousandths === null) {
        return amount;
    }
    const pence = roundMoneyDiv(tenThousandths, 100n);
    const digits = (pence < 0n ? -pence : pence).toString().padStart(3, '0');
    const major = digits.slice(0, -2);
    const text = `${options.grouped ? groupThousands(major) : major}.${digits.slice(-2)}`;
    return pence < 0n ? `-${text}` : text;
};

/** Renders an already-rounded server `amount` verbatim, adding only the symbol and grouping. */
export const formatVerbatimMoneyCurrency = (amount: string, currency: string = 'GBP'): string => {
    const negative = amount.startsWith('-');
    const [major, minor] = (negative ? amount.slice(1) : amount).split('.');
    const text = `${currencySymbol(currency)}${groupThousands(major)}${minor === undefined ? '' : `.${minor}`}`;
    return negative ? `-${text}` : text;
};

/** formatMoneyDisplay with the currency symbol and thousands grouping, e.g. "£1,234.57". */
export const formatMoneyCurrency = (amount: string, currency: string = 'GBP'): string =>
    formatVerbatimMoneyCurrency(formatMoneyDisplay(amount), currency);

/** A unit price kept at its stored four places, e.g. "£8.3333"; unparseable input is shown as given. */
export const formatFourPlacesCurrency = (amount: string, currency: string = 'GBP'): string => {
    const tenThousandths = parseTenThousandths(amount);
    return formatVerbatimMoneyCurrency(
        tenThousandths === null ? amount : formatTenThousandths(tenThousandths),
        currency,
    );
};
